// UI copy is localized via the account:usage.* catalog (all 6 locales). Tool/object_type names in
// OBJECT_TYPE_LABELS intentionally stay English (product feature names).
//
// Account → Usage tab. Renders the admin-only Team Usage Report
// (GET /v1/teams/usage-report, BE REF-5515). Semantics that MUST NOT drift (handover §6):
//   • "Images generated" = kpis.total_effective_images — the ONLY image metric, at every grain
//     (KPI, per tool, per member, trend). REF-6206 removed `generations` everywhere; it counted
//     billed JOBS, not images, and must not be resurrected as an "images" column.
//   • BE-enforced invariant: total_effective_images == SUM(actions[].effective_images)
//     == SUM(members[].effective_images). The tool and member tables MUST foot to the same
//     image total — if they don't, it's a BE bug, not something to paper over in the FE.
//   • members[].transactions is a SEPARATE, corrected metric (REF-6206 dedups
//     (object_type, object_id)). It reads HIGHER than the previous release for multi-tool
//     teams — that is the fix, not a regression. Keep it alongside Images; images ≫ transactions
//     is expected (one Studio run yields many images).
//   • "Avg credits / image" = kpis.avg_credits_per_image verbatim; null → "–" (never recompute)
//   • total_credits is the authority for "credits spent" (incl. bucket spill)
//   • distinct_object_types == actions.length (BE guarantees this — no remap)
//   • credits/image = total_credits / effective_images, guard divide-by-zero → "–"
//   • actions[] is DYNAMIC — object_type is a machine code; map via OBJECT_TYPE_LABELS, never hardcode
//   • errors carry a stable error_code (§5); branch on it, not on message text
//   • zero/empty is a VALID 200, not an error → clean empty state, no error toast
import React, { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { IconDownload, IconHelp, IconWarning } from "icons";
import Tooltip from "components/Tooltip";
import teamsService from "api/teams/teams.service";
import { ITeamUsageReportResponse, TeamUsageWindow } from "api/teams/teams.type";
import { useSnackbar } from "hooks/useSnackbar";
import { reasonErrorText } from "utils/reasonError";
import { formatNumber } from "i18n/formatters/number";
import i18n from "i18n";
import { objectTypeLabel } from "pages/Account/utils/usageLabels";
import UsageSpendDetail from "./UsageSpendDetail";

const DASH = "–";

// Window ids; labels are localized at render via t(`account:usage.windows.${id}`).
const WINDOWS: TeamUsageWindow[] = ["7d", "30d", "all"];

// Fixed series palette (reads on both light + dark surfaces). Must be at least as long as the
// number of distinct object_types the BE can surface, or seriesColor() cycles and two tools collide
// (11 tools vs 10 colors put Model Training on Studio's indigo — REF-5611). First 10 unchanged; the
// rest are extra well-separated hues for headroom as the object_type vocabulary grows.
const SERIES_COLORS = [
  "#6266F0", // indigo
  "#7CC242", // green
  "#F0A06B", // orange
  "#5AB0F0", // sky
  "#E86FB0", // pink
  "#9333EA", // purple (bolder/redder than the indigo above — was #8B5CF6 violet, too close to indigo)
  "#22C6A9", // teal
  "#F0C04A", // gold
  "#A0A6B0", // grey
  "#EF6B6B", // red
  "#A16207", // brown (11th tool slot — fuchsia here was too close to the pink #E86FB0 at index 4)
  "#0891B2", // cyan
  "#C026D3", // fuchsia
  "#65A30D", // olive
  "#DB2777", // deep rose
  "#475569", // slate (was #4338CA deep indigo — a 4th blue-purple that clashed with indigo)
];

const seriesColor = (index: number): string => SERIES_COLORS[index % SERIES_COLORS.length];

// start/end are "YYYY-MM-DD" — format in UTC so the label never drifts a day by local tz.
const formatDay = (day?: string | null): string => {
  if (!day) return "";
  const d = new Date(day);
  if (isNaN(d.getTime())) return day;

  return new Intl.DateTimeFormat(i18n.language || "en", { month: "short", day: "numeric", timeZone: "UTC" }).format(d);
};

// The selected window's date span. Adds the year only when the range crosses years (e.g. the
// 365-day window) so short ranges stay compact and the two ends never collapse to the same label.
const formatRange = (start?: string | null, end?: string | null): string => {
  if (!start || !end) return "";
  const s = new Date(start);
  const e = new Date(end);
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return `${start} – ${end}`;

  const lang = i18n.language || "en";
  const noYear: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", timeZone: "UTC" };
  const withYear: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" };
  const fmt = (d: Date, o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(lang, o).format(d);
  const sameYear = s.getUTCFullYear() === e.getUTCFullYear();

  return sameYear ? `${fmt(s, noYear)} – ${fmt(e, withYear)}` : `${fmt(s, withYear)} – ${fmt(e, withYear)}`;
};

// Guard the missing case → "–". Intl.NumberFormat.format(undefined) renders the literal "NaN",
// and REF-6206 caches each (team, window) payload in Redis for 300s — so for ~5 min after the BE
// deploy a stale envelope without `effective_images` can still arrive, and an FE that ships first
// sees it for longer. Cells go blank rather than "NaN".
const int = (value?: number | null): string => (typeof value === "number" && !isNaN(value) ? formatNumber(value) : DASH);

const oneDecimal = (value?: number | null): string => (typeof value === "number" && !isNaN(value) ? formatNumber(value, { maximumFractionDigits: 1 }) : DASH);

const creditsPerImage = (credits?: number | null, images?: number | null): string => (typeof credits === "number" && typeof images === "number" && images > 0 ? oneDecimal(credits / images) : DASH);

const Panel = ({ children, tone }: { children: React.ReactNode; tone?: "warn" }) => (
  <div className="flex flex-col gap-2.5">
    <div className={clsx("flex items-center gap-2.5 p-4 bg-body-100 border border-black-300 rounded-md", tone === "warn" ? "text-bodyMd text-trueBlack dark:text-white" : "text-bodyMd text-grey")}>
      {tone === "warn" && <IconWarning className="w-5 h-5 text-orange flex-shrink-0" />}
      {children}
    </div>
  </div>
);

// Info-icon tooltip (same pattern as ManageMembers' InfoTip; label copy stays raw English per
// this file's i18n deferral).
const InfoTip = ({ tip }: { tip: string }) => (
  <Tooltip content={tip} position="top" contentClassName="!whitespace-normal max-w-[220px] leading-snug">
    <IconHelp className="w-3.5 h-3.5 text-grey cursor-help" />
  </Tooltip>
);

const MetricCard = ({ label, value, tip }: { label: string; value: React.ReactNode; tip?: string }) => (
  <div className="flex flex-col gap-1.5 p-4 bg-body-100 border border-black-300 rounded-md">
    <span className="flex items-center gap-1 text-bodySm text-grey">
      {label}
      {tip && <InfoTip tip={tip} />}
    </span>
    <span className="text-h4 text-trueBlack dark:text-white">{value}</span>
  </div>
);

type Column = { key: string; header: React.ReactNode; cellClass?: string; render: (row: any) => React.ReactNode };

const DataTable = ({ columns, rows, minWidth, emptyLabel }: { columns: Column[]; rows: any[]; minWidth: string; emptyLabel: string }) => (
  <div className="overflow-x-auto">
    <div className={clsx("flex flex-col rounded-md border border-black-300", minWidth)}>
      <div className="flex items-center gap-3 px-3 py-2.5 bg-body-100 rounded-t-md border-b border-black-300 text-bodySm text-grey">
        {columns.map((c) => (
          <span key={c.key} className={c.cellClass}>
            {c.header}
          </span>
        ))}
      </div>
      {rows.length === 0 ? (
        <div className="px-3 py-6 text-center text-bodySm text-grey">{emptyLabel}</div>
      ) : (
        rows.map((row, i) => (
          <div key={i} className="flex items-center gap-3 px-3 py-2.5 border-b border-black-300 last:border-b-0 hover:bg-body-100/60 transition-colors text-bodySm text-trueBlack dark:text-white">
            {columns.map((c) => (
              <span key={c.key} className={c.cellClass}>
                {c.render(row)}
              </span>
            ))}
          </div>
        ))
      )}
    </div>
  </div>
);

// Group trend rows into a per-day stacked series. Skips null days (BE nullable). Series order
// follows report.actions (total_credits desc) so bar colors track the per-tool table; any
// trend-only codes are appended.
const buildChartData = (report: ITeamUsageReportResponse | null, metric: "credits" | "images") => {
  if (!report || report.trend.length === 0) return null;

  const dayMap = new Map<string, Record<string, number>>();
  report.trend.forEach((p) => {
    if (!p.day) return;
    const val = metric === "credits" ? p.credits : p.effective_images;
    const row = dayMap.get(p.day) ?? {};
    row[p.object_type] = (row[p.object_type] ?? 0) + val;
    dayMap.set(p.day, row);
  });

  if (dayMap.size === 0) return null;

  const days = [...dayMap.keys()].sort().map((day) => {
    const byAction = dayMap.get(day) as Record<string, number>;
    const total = Object.values(byAction).reduce((s, v) => s + v, 0);

    return { day, byAction, total };
  });

  const codes: string[] = [];
  report.actions.forEach((a) => codes.push(a.object_type));
  days.forEach((d) => Object.keys(d.byAction).forEach((c) => (codes.includes(c) ? null : codes.push(c))));
  const series = codes.filter((c) => days.some((d) => (d.byAction[c] ?? 0) > 0)).map((code, i) => ({ code, color: seriesColor(i) }));

  const maxTotal = days.reduce((m, d) => Math.max(m, d.total), 0);

  return { days, series, maxTotal };
};

const Usage = () => {
  const { t } = useTranslation();
  const { showError } = useSnackbar();
  const [selectedWindow, setSelectedWindow] = useState<TeamUsageWindow>("30d");
  const [report, setReport] = useState<ITeamUsageReportResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(true);
  const [noTeam, setNoTeam] = useState(false);
  // 503 from the BE (teams.report_unavailable) is "try again", NOT "you can't see this" — kept
  // separate from hasAccess so a dead DB never renders as a permission verdict (BE REF-6290).
  const [unavailable, setUnavailable] = useState(false);
  const [chartMetric, setChartMetric] = useState<"credits" | "images">("credits");
  const [isExporting, setIsExporting] = useState(false);

  const fetchReport = useCallback(
    async (win: TeamUsageWindow) => {
      setIsLoading(true);
      // Per-attempt, not sticky: it is checked before `noTeam`, so a leftover `true` from a
      // previous 503 would render "couldn't load" over a legitimate 404/empty answer.
      setUnavailable(false);
      try {
        const res = await teamsService.getUsageReport(win);
        // 401/503 resolve with _handled (Trap #1) rather than rejecting. They are NOT the same
        // answer: 503 means the report (or, since BE REF-6290, the gate query itself) failed, and
        // showing "only the owner can see this" there tells a paying owner they lost a permission
        // they still hold. HttpClient already raised the 503 snackbar, so only the panel changes.
        if (res?._handled) {
          if (res.responseCode === 503) {
            setUnavailable(true);
          } else {
            setHasAccess(false);
          }
          setReport(null);

          return;
        }

        setReport(res.responseData ?? null);
        setHasAccess(true);
        setNoTeam(false);
        setUnavailable(false);
      } catch (err: any) {
        // BE REF-5515 §5: branch on the stable, locale-independent error_code (HTTP status as a
        // fallback). usage-report is exempted in HttpClient so 403/404 reject here. Empty/zero is a
        // valid 200 handled below — never routed here — so this branch is only real failures.
        const status = err?.response?.status;
        const code = err?.response?.data?.responseData?.error_code;
        // BE REF-5515 restricted the report to the super admin → teams.superadmin_required (403).
        // teams.admin_required kept for back-compat; the status===403 fallback covers either.
        if (code === "teams.superadmin_required" || code === "teams.admin_required" || status === 403) {
          setHasAccess(false);
          setReport(null);
        } else if (code === "teams.team_not_found" || status === 404) {
          setNoTeam(true);
          setReport(null);
        } else {
          // teams.invalid_window (400, unreachable from the UI) + anything else → localized message.
          showError(reasonErrorText(err, t("account:usage.load_error")));
        }
      } finally {
        setIsLoading(false);
      }
    },
    [showError, t]
  );

  // Client-side .xlsx of the report already in hand — no export endpoint, no second read, so the
  // workbook is exactly the window on screen. Lazily imported: exceljs is ~1MB and the Account page
  // must not carry it just to render the tables.
  const handleExportExcel = useCallback(async () => {
    if (!report || isExporting) return;
    setIsExporting(true);
    try {
      const { exportUsageReportToExcel, usageExcelFileName } = await import("pages/Account/utils/usageExcel");
      const blob = await exportUsageReportToExcel({ report, objectTypeLabel, t });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.style.display = "none";
      link.href = url;
      link.download = usageExcelFileName(report);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      showError(t("account:usage.export.error"));
    } finally {
      setIsExporting(false);
    }
  }, [report, isExporting, showError, t]);

  useEffect(() => {
    fetchReport(selectedWindow);
  }, [selectedWindow, fetchReport]);

  const chart = useMemo(() => buildChartData(report, chartMetric), [report, chartMetric]);

  // First-load loader (subsequent window switches keep the previous report visible + dimmed).
  if (isLoading && !report) {
    return <Panel>{t("account:usage.loading")}</Panel>;
  }

  if (!hasAccess) {
    return <Panel tone="warn">{t("account:usage.super_admin_only")}</Panel>;
  }

  if (unavailable) {
    return <Panel tone="warn">{t("account:usage.load_error")}</Panel>;
  }

  if (noTeam) {
    return <Panel>{t("account:usage.no_team")}</Panel>;
  }

  if (!report) {
    return <Panel>{t("account:usage.no_data")}</Panel>;
  }

  const { kpis } = report;
  const isEmpty = report.actions.length === 0 && report.members.length === 0 && report.trend.length === 0;
  const rangeTitle = formatRange(report.start, report.end);

  const toolColumns: Column[] = [
    {
      key: "tool",
      header: t("account:usage.cols.tool"),
      cellClass: "flex-1 min-w-[160px] flex items-center gap-2 min-w-0",
      render: (a) => (
        <>
          <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: seriesColor(report.actions.indexOf(a)) }} />
          <span className="truncate" title={objectTypeLabel(a.object_type)}>
            {objectTypeLabel(a.object_type)}
          </span>
        </>
      ),
    },
    { key: "images", header: t("account:usage.cols.images"), cellClass: "w-24 flex-shrink-0 text-right", render: (a) => int(a.effective_images) },
    { key: "users", header: t("account:usage.cols.users"), cellClass: "w-20 flex-shrink-0 text-right", render: (a) => int(a.unique_users) },
    { key: "transactions", header: t("account:usage.cols.transactions"), cellClass: "w-28 flex-shrink-0 text-right", render: (a) => int(a.transactions) },
    { key: "credits", header: t("account:usage.cols.credits"), cellClass: "w-28 flex-shrink-0 text-right", render: (a) => int(a.total_credits) },
    { key: "avg", header: t("account:usage.cols.avg_credits"), cellClass: "w-24 flex-shrink-0 text-right", render: (a) => oneDecimal(a.avg_credits) },
    { key: "cpi", header: t("account:usage.cols.credits_per_image"), cellClass: "w-28 flex-shrink-0 text-right", render: (a) => creditsPerImage(a.total_credits, a.effective_images) },
  ];

  const memberColumns: Column[] = [
    {
      key: "member",
      header: t("account:usage.cols.member"),
      cellClass: "flex-1 min-w-[200px] flex flex-col min-w-0",
      render: (m) => (
        <>
          <span className="truncate">{m.name || m.email || t("account:usage.unknown_member")}</span>
          {m.email && m.name && <span className="text-grey truncate">{m.email}</span>}
        </>
      ),
    },
    { key: "images", header: t("account:usage.cols.images"), cellClass: "w-24 flex-shrink-0 text-right", render: (m) => int(m.effective_images) },
    { key: "transactions", header: t("account:usage.cols.transactions"), cellClass: "w-28 flex-shrink-0 text-right", render: (m) => int(m.transactions) },
    { key: "credits", header: t("account:usage.cols.credits"), cellClass: "w-28 flex-shrink-0 text-right", render: (m) => int(m.total_credits) },
    { key: "cpi", header: t("account:usage.cols.credits_per_image"), cellClass: "w-28 flex-shrink-0 text-right", render: (m) => creditsPerImage(m.total_credits, m.effective_images) },
    { key: "actions", header: t("account:usage.cols.distinct_tools"), cellClass: "w-24 flex-shrink-0 text-right", render: (m) => int(m.distinct_object_types) },
    {
      key: "top",
      header: t("account:usage.cols.top_tool"),
      cellClass: "w-40 flex-shrink-0 flex flex-col min-w-0",
      render: (m) =>
        m.top_object_type ? (
          <>
            <span className="truncate">{objectTypeLabel(m.top_object_type)}</span>
            <span className="text-grey">{t("account:usage.credits_count", { value: int(m.top_object_type_credits) })}</span>
          </>
        ) : (
          <span className="text-grey">{DASH}</span>
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      {/* Header: title + range + window selector */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex flex-col">
          <span className="text-h5 text-trueBlack dark:text-white">{rangeTitle}</span>
          <span className="text-bodySm text-grey">{t("account:usage.last_days", { days: report.window_days })}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 p-0.5 bg-body-100 border border-black-300 rounded-md">
            {WINDOWS.map((w) => (
              <button
                key={w}
                onClick={() => setSelectedWindow(w)}
                className={clsx(
                  "px-3 py-1.5 text-menu rounded transition-colors",
                  selectedWindow === w ? "bg-[#E4E4E4] dark:bg-black-300 text-trueBlack dark:text-white font-medium" : "text-grey hover:text-trueBlack dark:hover:text-white"
                )}
              >
                {t(`account:usage.windows.${w}`)}
              </button>
            ))}
          </div>
          {/* Exports the SELECTED window — the same rows the tables below show. Disabled while the
              period is empty: a workbook of four empty sheets is a worse answer than no button. */}
          <button
            type="button"
            onClick={handleExportExcel}
            disabled={isExporting || isEmpty}
            title={t("account:usage.export.button")}
            className="flex items-center gap-1.5 px-3 py-1.5 text-menu rounded-md border border-black-300 bg-body-100 text-trueBlack dark:text-white transition-colors hover:bg-body-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <IconDownload className="w-4 h-4" />
            {isExporting ? t("account:usage.export.in_progress") : t("account:usage.export.button")}
          </button>
        </div>
      </div>

      <div className={clsx("flex flex-col gap-8 transition-opacity", isLoading && "opacity-60 pointer-events-none")}>
        {isEmpty ? (
          <Panel>{t("account:usage.empty")}</Panel>
        ) : (
          <>
            {/* KPI tiles */}
            <div className="grid grid-cols-2 md:grid-cols-3 clg:grid-cols-4 gap-2.5">
              <MetricCard label={t("account:usage.kpis.images_generated")} value={int(kpis.total_effective_images)} tip={t("account:usage.kpis.images_generated_tip")} />
              <MetricCard label={t("account:usage.kpis.credits_spent")} value={int(kpis.total_credits)} tip={t("account:usage.kpis.credits_spent_tip")} />
              <MetricCard label={t("account:usage.kpis.active_users")} value={int(kpis.active_users)} tip={t("account:usage.kpis.active_users_tip")} />
              <MetricCard label={t("account:usage.kpis.tools_used")} value={int(kpis.distinct_object_types)} tip={t("account:usage.kpis.tools_used_tip")} />
            </div>

            {/* Daily trend chart */}
            {chart && (
              <div className="flex flex-col gap-3 p-4 bg-body-100 border border-black-300 rounded-md">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex flex-col">
                    <span className="text-bodyMd text-trueBlack dark:text-white">{t("account:usage.daily_trend")}</span>
                    <span className="text-bodySm text-grey">{t("account:usage.peak", { value: int(chart.maxTotal), metric: t(`account:usage.metric.${chartMetric}`) })}</span>
                  </div>
                  <div className="flex items-center gap-1 p-0.5 bg-body-200 rounded-md">
                    {(["credits", "images"] as const).map((m) => (
                      <button
                        key={m}
                        onClick={() => setChartMetric(m)}
                        className={clsx(
                          "px-2.5 py-1 text-bodySm rounded transition-colors capitalize",
                          chartMetric === m ? "bg-body-100 text-trueBlack dark:text-white font-medium" : "text-grey hover:text-trueBlack dark:hover:text-white"
                        )}
                      >
                        {t(`account:usage.metric.${m}`)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <div className="flex items-end gap-1 h-44 min-w-full">
                    {chart.days.map((d, i) => {
                      const step = Math.ceil(chart.days.length / 12);
                      const showLabel = i % step === 0 || i === chart.days.length - 1;
                      const rows = chart.series.filter((s) => (d.byAction[s.code] ?? 0) > 0);

                      return (
                        // Reuse the shared Tooltip (portal + floating-ui) so the styling matches every
                        // other tooltip and it isn't clipped by the overflow-x-auto scroll container.
                        <Tooltip
                          key={d.day}
                          position="top"
                          className="flex-1 min-w-[10px] h-full"
                          contentClassName="!whitespace-normal min-w-[170px]"
                          content={
                            <div className="flex flex-col gap-1">
                              <span className="font-medium">{formatDay(d.day)}</span>
                              {rows.map((s) => (
                                <span key={s.code} className="flex items-center gap-1.5">
                                  <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: s.color }} />
                                  <span className="flex-1">{objectTypeLabel(s.code)}</span>
                                  <span className="tabular-nums">{int(d.byAction[s.code])}</span>
                                </span>
                              ))}
                              <span className="flex items-center gap-1.5 border-t border-white/15 pt-1 mt-0.5">
                                <span className="flex-1 text-grey capitalize">{t("account:usage.total", { metric: t(`account:usage.metric.${chartMetric}`) })}</span>
                                <span className="tabular-nums">{int(d.total)}</span>
                              </span>
                            </div>
                          }
                        >
                          <div className="w-full h-full flex flex-col items-center gap-1 cursor-default">
                            <div className="flex flex-col-reverse w-full flex-1 rounded-t-sm overflow-hidden transition-opacity hover:opacity-80">
                              {chart.series.map((s) => {
                                const v = d.byAction[s.code] ?? 0;
                                if (!v) return null;

                                return <div key={s.code} style={{ height: `${chart.maxTotal ? (v / chart.maxTotal) * 100 : 0}%`, backgroundColor: s.color }} className="w-full flex-shrink-0" />;
                              })}
                            </div>
                            <span className="text-[10px] leading-none text-grey h-3 whitespace-nowrap">{showLabel ? formatDay(d.day) : ""}</span>
                          </div>
                        </Tooltip>
                      );
                    })}
                  </div>
                </div>

                {/* Legend */}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                  {chart.series.map((s) => (
                    <span key={s.code} className="flex items-center gap-1.5 text-bodySm text-grey">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: s.color }} />
                      {objectTypeLabel(s.code)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Per-tool breakdown */}
            <div className="flex flex-col gap-2">
              <span className="text-bodyMd text-trueBlack dark:text-white">{t("account:usage.by_tool")}</span>
              <DataTable columns={toolColumns} rows={report.actions} minWidth="min-w-[720px]" emptyLabel={t("account:usage.no_tool_usage")} />
            </div>

            {/* Per-member breakdown */}
            <div className="flex flex-col gap-2">
              <span className="text-bodyMd text-trueBlack dark:text-white">{t("account:usage.by_member")}</span>
              <DataTable columns={memberColumns} rows={report.members} minWidth="min-w-[600px]" emptyLabel={t("account:usage.no_member_usage")} />
            </div>
          </>
        )}
      </div>

      {/* REF-6975: per-transaction list with the images each spend produced. Its own date range
          (default last 30 days) and filters — independent of the window selector above. */}
      <UsageSpendDetail />
    </div>
  );
};

export default Usage;
